import Image from 'next/image'
import { FC, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useRouter } from 'next/router'

import Button, { EButtonStyleVariantClass } from '@/elements/button/Button'
import Anchor from '@/src/components/elements/anchor/Anchor'

const CookiePopup = styled.div`
    z-index: 3;
    position: fixed;
    bottom: 0;
    width: 100%;

    padding: 2rem 6rem;
    display: flex;

    background-color: ${props => props.theme.colors.white};

    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06);

    p {
        width: 85%;
    }

    @media (max-width: ${props =>
            // @ts-ignore
            props.theme.bps.md}) {
        position: sticky;
        top: 0;
        bottom: auto;
        box-shadow: 0 0 6px 6px rgba(0, 0, 0, 0.1), 0 2px 4px 1px rgba(0, 0, 0, 0.06);
        padding: 0.5rem 1rem 0.75rem 1rem;

        font-size: 0.75rem;

        gap: 1.25em;
        flex-direction: column;
        align-items: center;

        p {
            width: 100%;
        }
    }
`

const CookieConsentText = styled.div`
    display: flex;
    gap: 1.5rem;
    @media (max-width: ${props => props.theme.bps.md}) {
        gap: 0.85rem;
    }
`

const ActionButtonsContainer = styled.div`
    display: flex;
    gap: 1rem;

    @media (max-width: ${props => props.theme.bps.md}) {
        width: 100%;
        gap: 1.5rem;
        align-content: center;
        justify-content: center;
    }
`

const CookieImageContainer = styled.div`
    position: relative;
    height: 3rem;
    width: 3rem;

    @media (max-width: ${props => props.theme.bps.md}) {
        height: 2rem;
        width: 2rem;
    }
`

const CONSENT_COOKIE_NAME = 'isUserAcceptCookiesPolicy'

const getIsUserAcceptCookiesPolicy = (): boolean =>
    localStorage?.getItem(CONSENT_COOKIE_NAME) === String(true) || false

const setIsUserAcceptCookiesPolicy = (isAccepted: boolean): void =>
    localStorage?.setItem(CONSENT_COOKIE_NAME, String(isAccepted))

const MissingConsentContent = ({ handleOnConsent }) => (
    <CookiePopup>
        <CookieConsentText>
            <CookieImageContainer>
                <Image
                    src="/images/icons/cookie.svg"
                    alt="[cookie]"
                    width={24}
                    height={24}
                    loading="lazy"
                />
            </CookieImageContainer>
            <p>
                The website uses cookies to provide necessary functionality the user experience. Do
                you <strong>consent</strong> with our{' '}
                <Anchor isBold isColorized href="/legal/cookies">
                    cookies policy
                </Anchor>{' '}
                and{' '}
                <Anchor isBold isColorized href="/legal/privacy">
                    privacy policy
                </Anchor>
                ?
            </p>
        </CookieConsentText>

        <ActionButtonsContainer>
            <Button styleVariant={EButtonStyleVariantClass.Filled} onClick={handleOnConsent}>
                I consent
            </Button>
        </ActionButtonsContainer>
    </CookiePopup>
)

/* const HavingConsentContent = ({ handleOnDecline }) => {
    const { pathname } = useRouter()

    if (pathname !== '/legal/cookies') return null

    return (
        <CookiePopup>
            <CookieConsentText>
                <CookieImageContainer>
                    <Image
                        src="/images/icons/cookie.svg"
                        alt="[cookie]"
                        layout="fill"
                        loading="lazy"
                    />
                </CookieImageContainer>

                <p>
                    The website uses cookies to provide necessary functionality and enhance the user
                    experience. Please check more details about our{' '}
                    <Anchor isBold isColorized href="/legal/cookies">
                        cookies policy
                    </Anchor>{' '}
                    and{' '}
                    <Anchor isBold isColorized href="/legal/privacy">
                        privacy policy . You have <strong>granted us your consent</strong> to use
                        cookies.
                    </Anchor>
                </p>
            </CookieConsentText>

            <ActionButtonsContainer>
                <Button isOutlined onClick={handleOnDecline}>
                    Withdraw consent
                </Button>
            </ActionButtonsContainer>
        </CookiePopup>
    )
} */

const CookiesConsentPopup: FC = () => {
    const [isUserConsent, setIsUserConsent] = useState<boolean | null>(null)
    const [isVisible, setIsVisible] = useState(false)

    const handleToggleConsent = (isAccepted: boolean) => {
        setIsUserConsent(isAccepted)
        setIsUserAcceptCookiesPolicy(isAccepted)
    }

    const handleOnConsent = () => handleToggleConsent(true)

    useEffect(() => {
        const wasUserConsent = getIsUserAcceptCookiesPolicy()
        setIsUserConsent(wasUserConsent)

        // Show popup after a small delay to ensure hydration is complete
        const timer = setTimeout(() => {
            setIsVisible(true)
        }, 100)

        return () => clearTimeout(timer)
    }, [])

    // Don't render anything during server-side rendering or before the visibility check
    if (isUserConsent === null || !isVisible) {
        return null
    }

    // Only render when we have determined consent status and popup should be visible
    return !isUserConsent && <MissingConsentContent handleOnConsent={handleOnConsent} />
}

export default CookiesConsentPopup
